import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { FileText, Sparkles, Edit, Trash2, Eye, History, Upload, Lock, Unlock, Archive, RotateCcw, Printer, PenTool, GitBranch } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";
import { EmptyState } from "../components/shared/EmptyState";
import { TypeBadge } from "../components/shared/TypeBadge";
import { StatusBadge } from "../components/shared/StatusBadge";
import { BLUEPRINT_STATUSES } from "../components/shared/constants";
import { BlueprintUploadDialog } from "../components/shared/BlueprintUploadDialog";
import { BlueprintEditDialog } from "../components/shared/BlueprintEditDialog";
import { SearchFilterBar } from "../components/shared/SearchFilterBar";
import { DocumentViewerModal } from "../components/shared/DocumentViewerModal";
import { VersionHistoryModal } from "../components/shared/VersionHistoryModal";
import { ExportButton } from "../components/shared/ExportButton";
import { SortableHeader } from "../components/shared/SortableHeader";
import { isCADFile, CADFormatBadge, CADPreviewButton } from "../components/shared/CADViewer";
import { useDropdown, DropdownSelect } from "../components/shared/DropdownHooks";
import { LifecycleBadge, LifecycleTransitionDialog, LifecycleHistoryDialog } from "../components/shared/LifecycleWidgets";
import MarkupEditor from "../components/shared/MarkupEditor";

function ClassificationBadge({ classification }) {
  const { values } = useDropdown("blueprint_classifications");
  if (!classification) return <span className="text-xs text-[#CBD5E1]">—</span>;
  const match = values.find(v => v.value === classification);
  const color = match?.color || "#15803D";
  const label = match?.label || classification;
  return (
    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: `${color}15`, color, border: `1px solid ${color}40` }} data-testid="classification-badge">
      {label}
    </span>
  );
}

export default function BlueprintsPage({ onLogout, userRole, isFeatureEnabled }) {
  const [blueprints, setBlueprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editBlueprint, setEditBlueprint] = useState(null);
  const [editData, setEditData] = useState({ machine_number: "", blueprint_type: "", classification: "", notes: "", status: "" });
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [previewBlueprint, setPreviewBlueprint] = useState(null);
  const [versionBlueprint, setVersionBlueprint] = useState(null);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [bulkUploading, setBulkUploading] = useState(false);
  const [dynamicBpTypes, setDynamicBpTypes] = useState([]);

  // Fetch dynamic blueprint types
  useEffect(() => {
    axios.get(`${API}/admin/blueprint-types`, { headers: getAuthHeaders() })
      .then((r) => setDynamicBpTypes(r.data || []))
      .catch(() => {});
  }, []);
  const [bulkResults, setBulkResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const bulkInputRef = useRef(null);
  const [archiveBlueprint, setArchiveBlueprint] = useState(null);
  const [archiveData, setArchiveData] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [lifecycleBp, setLifecycleBp] = useState(null);
  const [lifecycleHistoryBp, setLifecycleHistoryBp] = useState(null);
  const [markupBp, setMarkupBp] = useState(null);

  const canEdit = userRole !== "hourly";
  const canDelete = userRole === "admin" || userRole === "superintendent";

  // Get current user ID for lock checks
  useEffect(() => {
    axios.get(`${API}/auth/me`, { headers: getAuthHeaders() })
      .then((r) => setCurrentUserId(r.data?.id))
      .catch(() => {});
  }, []);

  const fetchBlueprints = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (typeFilter) params.set("blueprint_type", typeFilter);
      if (statusFilter) params.set("status", statusFilter);
      if (sortBy) params.set("sort_by", sortBy);
      if (sortOrder) params.set("sort_order", sortOrder);
      const response = await axios.get(`${API}/blueprints?${params}`, { headers: getAuthHeaders() });
      setBlueprints(response.data);
    } catch { toast.error("Failed to load blueprints"); }
    finally { setLoading(false); }
  }, [search, typeFilter, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchBlueprints(), 300);
    return () => clearTimeout(debounce);
  }, [fetchBlueprints]);

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);
    try {
      await axios.post(`${API}/blueprints/upload`, formData, { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } });
      toast.success("Blueprint uploaded successfully");
      setSelectedFile(null);
      fetchBlueprints();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const handleBulkUpload = async (files) => {
    if (!files || files.length === 0) return;
    setBulkUploading(true);
    setBulkResults(null);
    const formData = new FormData();
    for (const file of files) {
      formData.append("files", file);
    }
    try {
      const response = await axios.post(`${API}/blueprints/bulk-upload`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      setBulkResults(response.data);
      if (response.data.summary.uploaded > 0) {
        toast.success(`${response.data.summary.uploaded} file(s) uploaded successfully`);
      }
      if (response.data.summary.duplicates > 0) {
        toast.warning(`${response.data.summary.duplicates} duplicate(s) skipped`);
      }
      if (response.data.summary.errors > 0) {
        toast.error(`${response.data.summary.errors} file(s) failed`);
      }
      fetchBlueprints();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk upload failed");
    } finally {
      setBulkUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleBulkUpload(files);
    }
  };

  const handleCategorize = async (id) => {
    try {
      toast.info("AI categorization in progress...");
      await axios.post(`${API}/blueprints/${id}/categorize`, {}, { headers: getAuthHeaders() });
      toast.success("Blueprint categorized successfully");
      fetchBlueprints();
    } catch { toast.error("Categorization failed"); }
  };

  const handleUpdate = async () => {
    if (!editBlueprint) return;
    try {
      await axios.put(`${API}/blueprints/${editBlueprint.id}`, editData, { headers: getAuthHeaders() });
      toast.success("Blueprint updated");
      setEditBlueprint(null);
      fetchBlueprints();
    } catch { toast.error("Update failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this blueprint?")) return;
    try {
      await axios.delete(`${API}/blueprints/${id}`, { headers: getAuthHeaders() });
      toast.success("Blueprint deleted");
      fetchBlueprints();
    } catch { toast.error("Delete failed"); }
  };

  const openEdit = (bp) => {
    if (bp.checked_out_by && bp.checked_out_by !== currentUserId) {
      toast.error(`Blueprint is checked out by ${bp.checked_out_by_name || "another user"}. Cannot edit.`);
      return;
    }
    setEditBlueprint(bp);
    setEditData({ machine_number: bp.machine_number || "", blueprint_type: bp.blueprint_type || "", classification: bp.classification || "", notes: bp.notes || "", status: bp.status || "" });
  };

  const handleCheckout = async (bp) => {
    try {
      await axios.post(`${API}/blueprints/${bp.id}/checkout`, {}, { headers: getAuthHeaders() });
      toast.success(`Blueprint checked out for editing`);
      fetchBlueprints();
    } catch (err) { toast.error(err.response?.data?.detail || "Checkout failed"); }
  };

  const handleCheckin = async (bp) => {
    try {
      await axios.post(`${API}/blueprints/${bp.id}/checkin`, {}, { headers: getAuthHeaders() });
      toast.success("Blueprint checked in — previous version archived");
      fetchBlueprints();
    } catch (err) { toast.error(err.response?.data?.detail || "Check-in failed"); }
  };

  const handleForceCheckin = async (bp) => {
    if (!window.confirm(`Force check-in? This will unlock the blueprint currently held by ${bp.checked_out_by_name}.`)) return;
    try {
      await axios.post(`${API}/blueprints/${bp.id}/force-checkin`, {}, { headers: getAuthHeaders() });
      toast.success("Blueprint force-checked-in");
      fetchBlueprints();
    } catch (err) { toast.error(err.response?.data?.detail || "Force check-in failed"); }
  };

  const openArchive = async (bp) => {
    setArchiveBlueprint(bp);
    try {
      const resp = await axios.get(`${API}/blueprints/${bp.id}/archive`, { headers: getAuthHeaders() });
      setArchiveData(resp.data || []);
    } catch { setArchiveData([]); }
  };

  const handleRestore = async (bp, archiveId, version) => {
    if (!window.confirm(`Restore blueprint to version ${version}? The current version will be archived first.`)) return;
    try {
      await axios.post(`${API}/blueprints/${bp.id}/restore/${archiveId}`, {}, { headers: getAuthHeaders() });
      toast.success(`Blueprint restored to version ${version}`);
      setArchiveBlueprint(null);
      fetchBlueprints();
    } catch (err) { toast.error(err.response?.data?.detail || "Restore failed"); }
  };

  const handleSort = (field, order) => { setSortBy(field); setSortOrder(order); };

  const handleLifecycleTransition = async (bpId, newState, comment) => {
    try {
      await axios.post(`${API}/blueprints/${bpId}/lifecycle/transition`, { new_state: newState, comment }, { headers: getAuthHeaders() });
      toast.success(`Document transitioned to ${newState.replace(/_/g, " ")}`);
      fetchBlueprints();
    } catch (err) { toast.error(err.response?.data?.detail || "Transition failed"); throw err; }
  };

  const clearFilters = () => { setSearch(""); setTypeFilter(""); setStatusFilter(""); };

  const typeOptions = dynamicBpTypes.map((t) => ({ value: t.name.toLowerCase().replace(/\s+/g, '_'), label: t.name }));
  const statusOptions = Object.entries(BLUEPRINT_STATUSES).map(([k, v]) => ({ value: k, label: v.label }));

  if (loading) {
    return (
      <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
        <div className="flex items-center justify-center h-64">
          <div className="text-[#64748B] font-mono">Loading blueprints...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-4" data-testid="blueprints-page">
        <PageHeader title="Blueprints" subtitle="Manage and categorize technical documents" testId="blueprints-title">
          <div className="flex gap-2">
            <ExportButton endpoint="/export/blueprints" filename="blueprints_export.csv" />
            {canEdit && <BlueprintUploadDialog selectedFile={selectedFile} setSelectedFile={setSelectedFile} onUpload={handleUpload} uploading={uploading} />}
            {canEdit && (
              <>
                <input type="file" multiple ref={bulkInputRef} className="hidden" onChange={(e) => handleBulkUpload(e.target.files)} data-testid="bulk-upload-input" />
                <Button variant="outline" className="rounded-none border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white" onClick={() => bulkInputRef.current?.click()} disabled={bulkUploading} data-testid="bulk-upload-button">
                  <Upload size={14} className="mr-1" />{bulkUploading ? "Uploading..." : "Bulk Upload"}
                </Button>
              </>
            )}
          </div>
        </PageHeader>

        {/* Drag & Drop Zone */}
        {canEdit && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`border-2 border-dashed p-4 text-center text-sm transition-colors ${
              dragOver ? "border-[#2563EB] bg-[#EFF6FF] text-[#2563EB]" : "border-[#E2E8F0] text-[#94A3B8]"
            }`}
            data-testid="drop-zone"
          >
            {bulkUploading ? "Uploading files..." : "Drag & drop blueprint files here for bulk upload"}
          </div>
        )}

        {/* Bulk Upload Results */}
        {bulkResults && (
          <div className="bg-white border border-[#E2E8F0] p-4 text-sm" data-testid="bulk-results">
            <div className="flex gap-4 mb-2">
              <span className="text-[#10B981] font-medium">{bulkResults.summary.uploaded} uploaded</span>
              <span className="text-[#F59E0B] font-medium">{bulkResults.summary.duplicates} duplicates</span>
              <span className="text-[#DC2626] font-medium">{bulkResults.summary.errors} errors</span>
            </div>
            <div className="space-y-1">
              {bulkResults.results.map((r, i) => (
                <div key={i} className={`text-xs font-mono ${r.status === "success" ? "text-[#10B981]" : r.status === "duplicate" ? "text-[#F59E0B]" : "text-[#DC2626]"}`}>
                  {r.filename}: {r.status}{r.detail ? ` — ${r.detail}` : ""}
                </div>
              ))}
            </div>
            <button onClick={() => setBulkResults(null)} className="text-xs text-[#64748B] hover:text-[#0F172A] mt-2">Dismiss</button>
          </div>
        )}

        <SearchFilterBar
          search={search}
          onSearchChange={setSearch}
          onClear={clearFilters}
          filters={[
            { key: "type", placeholder: "Types", value: typeFilter, onChange: setTypeFilter, options: typeOptions },
            { key: "status", placeholder: "Statuses", value: statusFilter, onChange: setStatusFilter, options: statusOptions },
          ]}
        />

        {blueprints.length === 0 ? (
          <EmptyState icon={FileText} message="No blueprints found. Upload your first document." />
        ) : (
          <div className="bg-white border border-[#E2E8F0]">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
                  <tr>
                    <SortableHeader label="Filename" field="original_filename" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} className="text-left" />
                    <SortableHeader label="Machine #" field="machine_number" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} className="text-left" />
                    <SortableHeader label="Type" field="blueprint_type" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} className="text-left" />
                    <th className="px-4 py-3 text-xs font-bold text-[#0F172A] tracking-wider uppercase text-left">Class.</th>
                    <SortableHeader label="Status" field="status" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} className="text-left" />
                    <th className="px-4 py-3 text-xs font-bold text-[#0F172A] tracking-wider uppercase text-left">Lifecycle</th>
                    <th className="px-4 py-3 text-xs font-bold text-[#0F172A] tracking-wider uppercase text-center">Lock</th>
                    <th className="px-4 py-3 text-xs font-bold text-[#0F172A] tracking-wider uppercase text-right">Ver</th>
                    <th className="px-6 py-3 text-xs font-bold text-[#0F172A] tracking-wider uppercase text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {blueprints.map((bp) => {
                    const isLockedByMe = bp.checked_out_by === currentUserId;
                    const isLockedByOther = bp.checked_out_by && bp.checked_out_by !== currentUserId;
                    const isLocked = !!bp.checked_out_by;
                    return (
                    <tr key={bp.id} className={`transition-colors duration-150 ${isLockedByOther ? "bg-[#FEF2F2]" : isLockedByMe ? "bg-[#FFFBEB]" : "hover:bg-[#FAFAFA]"}`} data-testid={`blueprint-row-${bp.id}`}>
                      <td className="px-6 py-4 text-sm text-[#0F172A]">
                        <button onClick={() => setPreviewBlueprint(bp)} className="flex items-center gap-2 hover:text-[#2563EB] transition-colors text-left" data-testid={`preview-button-${bp.id}`}>
                          <FileText size={16} className="text-[#64748B] shrink-0" />{bp.original_filename}
                          {isCADFile(bp.original_filename) && <CADFormatBadge filename={bp.original_filename} />}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm font-mono text-[#0F172A]">{bp.machine_number || "-"}</td>
                      <td className="px-6 py-4 text-sm"><TypeBadge type={bp.blueprint_type} /></td>
                      <td className="px-4 py-4 text-sm"><ClassificationBadge classification={bp.classification} /></td>
                      <td className="px-6 py-4 text-sm"><StatusBadge status={bp.status} colorMap={BLUEPRINT_STATUSES} /></td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1.5">
                          <LifecycleBadge state={bp.lifecycle_state || "draft"} />
                          <button onClick={() => setLifecycleHistoryBp(bp)} className="text-[#94A3B8] hover:text-[#2563EB]" title="Lifecycle history" data-testid={`lifecycle-history-${bp.id}`}><GitBranch size={12} /></button>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        {isLocked ? (
                          <span className="inline-flex items-center gap-1 text-xs font-bold" title={`Checked out by ${bp.checked_out_by_name}`} data-testid={`lock-badge-${bp.id}`}>
                            <Lock size={14} className={isLockedByMe ? "text-[#D97706]" : "text-[#DC2626]"} />
                            <span className={isLockedByMe ? "text-[#D97706]" : "text-[#DC2626]"}>{isLockedByMe ? "You" : bp.checked_out_by_name}</span>
                          </span>
                        ) : (
                          <span className="text-[#10B981]" data-testid={`unlocked-badge-${bp.id}`}><Unlock size={14} /></span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <button onClick={() => setVersionBlueprint(bp)} className="text-xs font-mono text-[#2563EB] hover:underline cursor-pointer" data-testid={`version-badge-${bp.id}`}>
                          v{bp.version || 1}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-right text-sm">
                        <div className="flex items-center justify-end gap-1">
                          {isCADFile(bp.original_filename) && <CADPreviewButton blueprintId={bp.id} filename={bp.original_filename} />}
                          <Button size="sm" variant="ghost" onClick={async () => { await axios.post(`${API}/print-log`, { blueprint_id: bp.id, filename: bp.original_filename, copies: 1 }, { headers: getAuthHeaders() }).catch(() => {}); window.print(); }} className="text-[#64748B] hover:text-[#0F172A]" data-testid={`print-button-${bp.id}`} title="Print & log"><Printer size={14} /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setPreviewBlueprint(bp)} className="text-[#64748B] hover:text-[#2563EB]" data-testid={`view-button-${bp.id}`}><Eye size={14} /></Button>
                          {canEdit && !isLocked && (
                            <Button size="sm" variant="outline" onClick={() => handleCheckout(bp)} className="rounded-none border-[#D97706] text-[#D97706] hover:bg-[#D97706] hover:text-white" data-testid={`checkout-button-${bp.id}`} title="Check out for editing"><Lock size={14} /></Button>
                          )}
                          {canEdit && isLockedByMe && (
                            <Button size="sm" variant="outline" onClick={() => handleCheckin(bp)} className="rounded-none border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white" data-testid={`checkin-button-${bp.id}`} title="Check in (archive & unlock)"><Unlock size={14} /></Button>
                          )}
                          {canDelete && isLockedByOther && (
                            <Button size="sm" variant="outline" onClick={() => handleForceCheckin(bp)} className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white" data-testid={`force-checkin-button-${bp.id}`} title="Force check-in (admin)"><Unlock size={14} /></Button>
                          )}
                          {canEdit && isLockedByMe && <Button size="sm" variant="outline" onClick={() => openEdit(bp)} className="rounded-none border-[#E2E8F0]" data-testid={`edit-button-${bp.id}`}><Edit size={14} /></Button>}
                          {canEdit && !isLocked && bp.status === "pending" && (
                            <Button size="sm" onClick={() => handleCategorize(bp.id)} className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid={`categorize-button-${bp.id}`}><Sparkles size={14} /></Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => openArchive(bp)} className="text-[#64748B] hover:text-[#D97706]" data-testid={`archive-button-${bp.id}`} title="View archive"><Archive size={14} /></Button>
                          <Button size="sm" variant="outline" onClick={() => setLifecycleBp(bp)} className="rounded-none border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white" data-testid={`lifecycle-button-${bp.id}`} title="Lifecycle transition"><GitBranch size={14} /></Button>
                          <Button size="sm" variant="outline" onClick={() => setMarkupBp(bp)} className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white" data-testid={`redline-button-${bp.id}`} title="Digital Redline"><PenTool size={14} /></Button>
                          {canDelete && !isLocked && <Button size="sm" variant="outline" onClick={() => handleDelete(bp.id)} className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white" data-testid={`delete-button-${bp.id}`}><Trash2 size={14} /></Button>}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <BlueprintEditDialog editBlueprint={editBlueprint} editData={editData} setEditData={setEditData} onUpdate={handleUpdate} onClose={() => setEditBlueprint(null)} />
      <DocumentViewerModal open={!!previewBlueprint} onClose={() => setPreviewBlueprint(null)} blueprint={previewBlueprint} />
      <VersionHistoryModal open={!!versionBlueprint} onClose={() => setVersionBlueprint(null)} blueprint={versionBlueprint} />

      {/* Archive Modal */}
      {archiveBlueprint && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setArchiveBlueprint(null)}>
          <div className="bg-white rounded-sm border border-[#E7E5E4] w-full max-w-3xl max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="archive-modal">
            <div className="p-6 border-b border-[#E7E5E4]">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-heading font-bold text-xl text-[#1C1917]">Version Archive</h2>
                  <p className="text-sm text-[#78716C] mt-1">{archiveBlueprint.original_filename} — Current: v{archiveBlueprint.version || 1}</p>
                </div>
                <Button variant="ghost" onClick={() => setArchiveBlueprint(null)} className="text-[#78716C]">&times;</Button>
              </div>
            </div>
            <div className="p-6">
              {archiveData.length === 0 ? (
                <div className="text-center py-8 text-[#78716C]">
                  <Archive size={32} className="mx-auto mb-3 text-[#A8A29E]" />
                  <p>No archived versions yet.</p>
                  <p className="text-xs mt-1">Archive entries are created when a blueprint is checked in after editing.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {archiveData.map((entry) => (
                    <div key={entry.id} className="border border-[#E7E5E4] p-4" data-testid={`archive-entry-${entry.id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-3">
                            <span className="px-2 py-0.5 text-xs font-bold bg-[#F5F5F4] text-[#1C1917] font-mono">v{entry.version}</span>
                            <span className="text-sm text-[#1C1917] font-medium">{entry.original_filename}</span>
                          </div>
                          <div className="text-xs text-[#78716C] mt-1">
                            Archived by {entry.archived_by_name} on {new Date(entry.archived_at).toLocaleString()}
                          </div>
                          {entry.archived_reason && <div className="text-xs text-[#D97706] mt-1">{entry.archived_reason}</div>}
                          <div className="flex gap-4 mt-2 text-xs text-[#78716C]">
                            {entry.machine_number && <span>Machine: <strong>{entry.machine_number}</strong></span>}
                            {entry.blueprint_type && <span>Type: <strong>{entry.blueprint_type}</strong></span>}
                            {entry.status && <span>Status: <strong>{entry.status}</strong></span>}
                          </div>
                        </div>
                        {canDelete && (
                          <Button size="sm" variant="outline" onClick={() => handleRestore(archiveBlueprint, entry.id, entry.version)} className="rounded-none border-[#D97706] text-[#D97706] hover:bg-[#D97706] hover:text-white" data-testid={`restore-button-${entry.id}`}>
                            <RotateCcw size={14} className="mr-1" />Restore
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lifecycle Transition Dialog */}
      <LifecycleTransitionDialog
        open={!!lifecycleBp}
        onClose={() => setLifecycleBp(null)}
        blueprint={lifecycleBp}
        onConfirm={handleLifecycleTransition}
      />

      {/* Lifecycle History Dialog */}
      <LifecycleHistoryDialog
        open={!!lifecycleHistoryBp}
        onClose={() => setLifecycleHistoryBp(null)}
        blueprintId={lifecycleHistoryBp?.id}
      />

      {/* Markup/Redline Editor */}
      <MarkupEditor
        open={!!markupBp}
        onClose={() => setMarkupBp(null)}
        blueprint={markupBp}
      />
    </Layout>
  );
}
